import reduce from "lodash/reduce";
import { type ReactNode, useMemo, useState, useCallback } from "react";

import { FIELD_CONFIG } from "@/components/screener-builder/review/competency-modal/assessment-question-field";
import {
  AssessmentQuestionFields,
  type GenerateButtonState,
  type ReviewPageCompetency,
} from "@/types/screener-builder";
import type { Nullable } from "@/types/utils";
import { anyEmptyStrings } from "@/utils/helpers";

enum ValidationStatus {
  Unvalidated = "unvalidated",
  Successful = "successful",
  Failed = "failed",
}

interface UseCompetencyChangeTrackingReturn {
  needsValidation: boolean;
  markValidationSuccess: () => void;
  markValidationFailure: () => void;
  validationStatus: ValidationStatus;
}

interface UseGenerateButtonStatesProps {
  competencyName: string;
  competencyNameError: Nullable<ReactNode>;
  competencyDefinition: string;
  competencyDefinitionError: Nullable<ReactNode>;
  question: string;
  questionError: ReactNode | null;
}

interface GenerateButtonStates {
  [AssessmentQuestionFields.Competency]: GenerateButtonState<AssessmentQuestionFields.Competency>;
  [AssessmentQuestionFields.Definition]: GenerateButtonState<AssessmentQuestionFields.Definition>;
  [AssessmentQuestionFields.Question]: GenerateButtonState<AssessmentQuestionFields.Question>;
}

const getFieldGenerateButtonState = <Field extends AssessmentQuestionFields>(
  field: Field,
  value: string,
  allFieldsStatuses: {
    [key in AssessmentQuestionFields]: { hasErrors: boolean; value: string };
  },
): GenerateButtonState<Field> => {
  const { disabledGenerateTooltip, getGenerateInputs } = FIELD_CONFIG[field];
  const errorCount = reduce(
    allFieldsStatuses,
    (count, { hasErrors }) => (hasErrors ? count + 1 : count),
    0,
  );
  const isRegenerate = !anyEmptyStrings(value);

  // If there are no errors, or the only error is on the current field, use
  // standard generation requirements
  if (
    errorCount === 0 ||
    // Because a field cannot be an input to generate itself, we can enable
    // generation for a field with an error. This allows the user to overwrite
    // a field with a programmatic error with a valid value from the LLM
    (errorCount === 1 && allFieldsStatuses[field].hasErrors)
  ) {
    const generateInput = getGenerateInputs(
      allFieldsStatuses[AssessmentQuestionFields.Competency].value,
      allFieldsStatuses[AssessmentQuestionFields.Definition].value,
      allFieldsStatuses[AssessmentQuestionFields.Question].value,
    );

    return {
      disabled: generateInput === null,
      generateInput,
      isRegenerate,
      tooltip: disabledGenerateTooltip,
    };
  }

  // If there are multiple errors, or errors on other fields, generation is
  // disabled
  return {
    disabled: true,
    generateInput: null,
    isRegenerate,
    tooltip: "Please fix validation errors to enable generation",
  };
};

export const useGenerateButtonStates = ({
  competencyName,
  competencyNameError,
  competencyDefinition,
  competencyDefinitionError,
  question,
  questionError,
}: UseGenerateButtonStatesProps): GenerateButtonStates => {
  const allFieldsStatus = useMemo(
    () => ({
      [AssessmentQuestionFields.Competency]: {
        hasErrors: competencyNameError !== null,
        value: competencyName,
      },
      [AssessmentQuestionFields.Definition]: {
        hasErrors: competencyDefinitionError !== null,
        value: competencyDefinition,
      },
      [AssessmentQuestionFields.Question]: {
        hasErrors: questionError !== null,
        value: question,
      },
    }),
    [
      competencyDefinition,
      competencyDefinitionError,
      competencyName,
      competencyNameError,
      question,
      questionError,
    ],
  );

  return useMemo(
    () => ({
      [AssessmentQuestionFields.Competency]: getFieldGenerateButtonState(
        AssessmentQuestionFields.Competency,
        competencyName,
        allFieldsStatus,
      ),
      [AssessmentQuestionFields.Definition]: getFieldGenerateButtonState(
        AssessmentQuestionFields.Definition,
        competencyDefinition,
        allFieldsStatus,
      ),
      [AssessmentQuestionFields.Question]: getFieldGenerateButtonState(
        AssessmentQuestionFields.Question,
        question,
        allFieldsStatus,
      ),
    }),
    [allFieldsStatus, competencyDefinition, competencyName, question],
  );
};

interface UseCompetencyChangeTrackingParams {
  competency: ReviewPageCompetency;
  competencyName: string;
  competencyDefinition: string;
  question: string;
}

export const useCompetencyChangeTracking = ({
  competency,
  competencyName,
  competencyDefinition,
  question,
}: UseCompetencyChangeTrackingParams): UseCompetencyChangeTrackingReturn => {
  const selectedQuestion =
    competency.questions.find(({ isSelected }) => isSelected)?.question ?? "";

  const [validationStatus, setValidationStatus] = useState<ValidationStatus>(
    ValidationStatus.Unvalidated,
  );

  // Track last validated values
  const [lastValidatedValues, setLastValidatedValues] = useState({
    competencyName: competency.name,
    competencyDefinition: competency.definition,
    question: selectedQuestion,
  });

  const needsValidation = useMemo(() => {
    // If validation previously failed, we need to validate again
    if (validationStatus === ValidationStatus.Failed) {
      return true;
    }

    return (
      lastValidatedValues.competencyName !== competencyName ||
      lastValidatedValues.competencyDefinition !== competencyDefinition ||
      lastValidatedValues.question !== question
    );
  }, [
    validationStatus,
    lastValidatedValues,
    competencyName,
    competencyDefinition,
    question,
  ]);

  const markValidationSuccess = useCallback(() => {
    setValidationStatus(ValidationStatus.Successful);
    setLastValidatedValues({
      competencyName,
      competencyDefinition,
      question,
    });
  }, [competencyName, competencyDefinition, question]);

  const markValidationFailure = useCallback(() => {
    setValidationStatus(ValidationStatus.Failed);
  }, []);

  return {
    needsValidation,
    markValidationSuccess,
    markValidationFailure,
    validationStatus,
  };
};
